const LABEL_SECTION_REGEX = /(?:^|\.\s+)([A-Z][^.\n:]{1,58}?):\s+/g;
const LETTERED_ITEM_REGEX = /\(([A-Z])\)\s+/g;

function matchAllRegex(text: string, regex: RegExp): RegExpExecArray[] {
  const flags = regex.flags.includes('g') ? regex.flags : `${regex.flags}g`;
  const pattern = new RegExp(regex.source, flags);
  const matches: RegExpExecArray[] = [];
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    matches.push(match);
    if (match[0].length === 0) {
      pattern.lastIndex += 1;
    }
  }

  return matches;
}

function hasMarkdownStructure(text: string): boolean {
  return (
    /^#{1,3}\s/m.test(text) ||
    /^[-*]\s/m.test(text) ||
    /^\d+\.\s/m.test(text) ||
    text.includes('\n\n')
  );
}

function formatLetteredItems(body: string): string | null {
  const matches = matchAllRegex(body, LETTERED_ITEM_REGEX);
  if (matches.length < 2) {
    return null;
  }

  const items: string[] = [];
  for (let index = 0; index < matches.length; index += 1) {
    const match = matches[index];
    const start = match.index! + match[0].length;
    const end = index + 1 < matches.length ? matches[index + 1].index! : body.length;
    let item = body.slice(start, end).trim().replace(/[;.]\s*$/, '');

    const colonIndex = item.indexOf(':');
    if (colonIndex > 0 && colonIndex < 80) {
      const label = item.slice(0, colonIndex).trim();
      const value = item.slice(colonIndex + 1).trim();
      items.push(`- **${label}:** ${value}`);
    } else {
      items.push(`- **(${match[1]})** ${item}`);
    }
  }

  return items.join('\n');
}

function formatSemicolonList(body: string): string | null {
  if (!body.includes(';')) {
    return null;
  }

  const parts = body
    .split(/;\s+/)
    .map((part) => part.trim().replace(/\.\s*$/, ''))
    .filter(Boolean);

  if (parts.length < 2 || parts.length > 8 || parts.some((part) => part.length > 250)) {
    return null;
  }

  return parts.map((part) => `- ${part}`).join('\n');
}

function formatCommaList(label: string, body: string): string | null {
  if (!/(missing|gap|risk|driver|requirement|assumption|metric)/i.test(label)) {
    return null;
  }

  const parts = body
    .split(/,\s+/)
    .map((part) => part.trim().replace(/\.\s*$/, ''))
    .filter(Boolean);

  if (parts.length < 3 || parts.some((part) => part.length > 120)) {
    return null;
  }

  return parts.map((part) => `- ${part}`).join('\n');
}

function splitTrailingParagraph(body: string): string[] {
  const match = body.match(/^(.+?\.\s+)([A-Z].+)$/);
  if (!match) {
    return [body];
  }

  const lead = match[1].trim();
  const trailing = match[2].trim();
  if (lead.length >= body.length * 0.75 || trailing.length < 24) {
    return [body];
  }

  return [lead, trailing];
}

function formatSectionBody(label: string | null, body: string): string {
  const trimmedBody = body.trim();
  if (!trimmedBody) {
    return '';
  }

  const lettered = formatLetteredItems(trimmedBody);
  if (lettered) {
    return lettered;
  }

  const semicolonList = formatSemicolonList(trimmedBody);
  if (semicolonList) {
    return semicolonList;
  }

  if (label) {
    const commaList = formatCommaList(label, trimmedBody);
    if (commaList) {
      return commaList;
    }
  }

  return trimmedBody;
}

function splitLabeledSections(text: string): Array<{ label: string | null; body: string }> {
  const matches = matchAllRegex(text, LABEL_SECTION_REGEX);

  if (matches.length === 0) {
    return [{ label: null, body: text }];
  }

  const sections: Array<{ label: string | null; body: string }> = [];
  const firstIndex = matches[0].index ?? 0;

  if (firstIndex > 0) {
    const intro = text.slice(0, firstIndex).trim();
    if (intro) {
      sections.push({ label: null, body: intro });
    }
  }

  for (let index = 0; index < matches.length; index += 1) {
    const match = matches[index];
    const label = match[1].trim();
    const bodyStart = match.index! + match[0].length;
    const bodyEnd = index + 1 < matches.length ? matches[index + 1].index! : text.length;
    const body = text.slice(bodyStart, bodyEnd).trim();

    sections.push({ label, body });
  }

  return sections;
}

export function formatBriefingProseForDisplay(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) {
    return '';
  }

  if (hasMarkdownStructure(trimmed)) {
    return trimmed;
  }

  const sections = splitLabeledSections(trimmed);
  if (sections.length === 1 && !sections[0].label) {
    const onlyBody = sections[0].body;
    const lettered = formatLetteredItems(onlyBody);
    if (lettered) {
      return lettered;
    }

    const semicolonList = formatSemicolonList(onlyBody);
    if (semicolonList) {
      return semicolonList;
    }

    return onlyBody;
  }

  const blocks: string[] = [];

  for (const section of sections) {
    const formattedBody = formatSectionBody(section.label, section.body);
    if (!formattedBody) {
      continue;
    }

    const bodyParts =
      section.label && !formattedBody.startsWith('-')
        ? splitTrailingParagraph(formattedBody)
        : [formattedBody];

    if (section.label) {
      blocks.push(`### ${section.label}\n\n${bodyParts.join('\n\n')}`);
    } else {
      blocks.push(bodyParts.join('\n\n'));
    }
  }

  return blocks.join('\n\n');
}
